/**
 * Hook: useProgramacionesList
 *
 * Gestión de paginado, filtros y caché para los dos tabs de Programaciones:
 *   - Tab 1 "misOT"      → /Programacion/paginar
 *   - Tab 2 "otAbiertas" → /Ot/paginar (con otAbierta:true)
 *
 * Regla de caché:
 *   - Solo se mantiene caché del tab activo.
 *   - Al cambiar de tab se limpia el caché del tab abandonado.
 *   - Al cambiar filtros o página se limpia el caché del tab activo y se recarga.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useProgramacionesCache } from './useProgramacionesCache';
import { paginate } from '../api/paginate';
import { getDataList } from '../api/common';
import { VIEWPROGRAMACION, VIEWALLPROGRAMACION } from '../components/Roles/ConstantRoute';
import ValidarPermiso from '../components/Roles/ValidarPermiso';
import { Alertassw } from '../components/Alertas/Alertassw';

// ─── Valores por defecto ─────────────────────────────────────────────────────

const DEFAULT_TAB1_FILTERS = {
  page: 1,
  perPage: 20,
  estado: '',
  numeroOrden: '',
  ciudad: '',
  cliente: '',
  lider: '',
  fechaDesde: '',
  fechaHasta: ''
};

const DEFAULT_TAB2_FILTERS = {
  page: 1,
  perPage: 20,
  estado: '',
  numeroOrden: '',
  ciudad: '',
  cliente: '',
  lider: '',
  fechaDesde: '',
  fechaHasta: ''
};

const EMPTY_DATA = {
  results: [],
  total: 0,
  totalPages: 0,
  next: null,
  prev: null,
  page: 1,
  perPage: 20
};

// ─── Hook principal ───────────────────────────────────────────────────────────

export const useProgramacionesList = () => {
  const { userData } = useAuth();
  const { getCached, setCached, invalidate } = useProgramacionesCache();

  const userId   = userData?._id;
  const permisos = userData?.role?.permisos || [];

  const CACHE_KEY_TAB1  = `programaciones_mis_ot_${userId}`;
  const CACHE_KEY_TAB2  = `programaciones_ot_abiertas_${userId}`;
  const TAB_STORAGE_KEY = `programaciones_active_tab_${userId}`;
  const FILTER_KEY_TAB1 = `programaciones_filters_tab1_${userId}`;
  const FILTER_KEY_TAB2 = `programaciones_filters_tab2_${userId}`;

  // ── Estado del tab activo (persiste en localStorage) ──────────────────────
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem(TAB_STORAGE_KEY) || 'misOT'
  );

  // ── Estado Tab 1 ──────────────────────────────────────────────────────────
  const [tab1Filters, setTab1Filters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY_TAB1);
      return saved ? { ...DEFAULT_TAB1_FILTERS, ...JSON.parse(saved) } : DEFAULT_TAB1_FILTERS;
    } catch { return DEFAULT_TAB1_FILTERS; }
  });
  const [tab1Data,    setTab1Data]    = useState(EMPTY_DATA);
  const [tab1Loading, setTab1Loading] = useState(false);

  // ── Estado Tab 2 ──────────────────────────────────────────────────────────
  const [tab2Filters, setTab2Filters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY_TAB2);
      return saved ? { ...DEFAULT_TAB2_FILTERS, ...JSON.parse(saved) } : DEFAULT_TAB2_FILTERS;
    } catch { return DEFAULT_TAB2_FILTERS; }
  });
  const [tab2Data,    setTab2Data]    = useState(EMPTY_DATA);
  const [tab2Loading, setTab2Loading] = useState(false);

  // ── Helper: buscar IDs de usuarios por condiciones ──────────────────────
  const lookupUserIds = useCallback(async (userConditions) => {
    try {
      const res = await getDataList('usuarios', { conditions: userConditions });
      const list = res.data.data || [];
      return list.length ? list.map(u => u._id) : ['000000000000000000000000'];
    } catch {
      return ['000000000000000000000000'];
    }
  }, []);

  // ── Cargador principal ───────────────────────────────────────────────────
  const doLoadData = useCallback(
    async (tab, filters, forceRefresh = false) => {
      const isTab1    = tab === 'misOT';
      const cacheKey  = isTab1 ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2;
      const setLoading = isTab1 ? setTab1Loading : setTab2Loading;
      const setData   = isTab1 ? setTab1Data    : setTab2Data;

      // Sin permiso → no cargar Tab 1
      if (isTab1) {
        const hasPerm =
          ValidarPermiso(VIEWPROGRAMACION,    permisos) !== -1 ||
          ValidarPermiso(VIEWALLPROGRAMACION, permisos) !== -1;
        if (!hasPerm) return;
      }

      // Usar caché si está vigente
      if (!forceRefresh) {
        const cached = getCached(cacheKey);
        if (cached) {
          setData(cached);
          return;
        }
      }

      setLoading(true);
      try {
        const { page, perPage } = filters;
        const conditions = {};

        // Helper: construye el `$or` de nombre/apellido para buscar un usuario
        // por "líder". Devuelve null si no hay texto de búsqueda.
        const liderQueryFromText = (txt) => {
          const t = (txt || '').trim();
          if (!t) return null;
          return { $or: [
            { nombre:   { $regex: t, $options: 'i' } },
            { apellido: { $regex: t, $options: 'i' } }
          ]};
        };

        if (isTab1) {
          // ── Permisos ──────────────────────────────────────────────────────
          if (ValidarPermiso(VIEWPROGRAMACION, permisos) !== -1) {
            conditions.responsable = userId;
          }

          // ── Ciudad + Cliente → lookup en Usuarios (clientInformation) ────
          if (filters.ciudad || filters.cliente) {
            const userQuery = {};
            if (filters.ciudad)  userQuery.ciudad = { $regex: filters.ciudad, $options: 'i' };
            if (filters.cliente) userQuery.nombre = { $regex: filters.cliente, $options: 'i' };
            const ids = await lookupUserIds(userQuery);
            conditions.clientInformation = { $in: ids };
          }

          // ── Campos directos ───────────────────────────────────────────────
          if (filters.estado)      conditions.estado      = filters.estado;
          if (filters.numeroOrden) conditions.numeroOrden = { $regex: filters.numeroOrden, $options: 'i' };

          if (filters.fechaDesde || filters.fechaHasta) {
            conditions.fechaInicio = {};
            if (filters.fechaDesde) { const d = new Date(filters.fechaDesde); d.setHours(0,0,0,0);       conditions.fechaInicio.$gte = d.toISOString(); }
            if (filters.fechaHasta) { const d = new Date(filters.fechaHasta); d.setHours(23,59,59,999);  conditions.fechaInicio.$lte = d.toISOString(); }
          }

          // ── Líder (responsable de la programación) ──────────────────────
          // Sobrescribe el filtro restrictivo de VIEWPROGRAMACION; el usuario
          // escribió explícitamente un líder a buscar.
          const liderQ = liderQueryFromText(filters.lider);
          if (liderQ) {
            const ids = await lookupUserIds(liderQ);
            conditions.responsable = { $in: ids };
          }
        } else {
          // ── Tab 2: OTs Abiertas ───────────────────────────────────────────
          conditions.otAbierta = true;
          conditions.otType    = 'Parent';

          // Ciudad — campo denormalizado directo
          if (filters.ciudad) conditions.ciudadCliente = { $regex: filters.ciudad, $options: 'i' };

          // Cliente — lookup en Usuarios por nombre
          if (filters.cliente) {
            const ids = await lookupUserIds({ nombre: { $regex: filters.cliente, $options: 'i' } });
            conditions.cliente = { $in: ids };
          }

          if (filters.estado)      conditions.estado      = filters.estado;
          if (filters.numeroOrden) conditions.numeroOrden = { $regex: filters.numeroOrden, $options: 'i' };

          // Fechas sobre fechaEntrega
          if (filters.fechaDesde || filters.fechaHasta) {
            conditions.fechaEntrega = {};
            if (filters.fechaDesde) { const d = new Date(filters.fechaDesde); d.setHours(0,0,0,0);       conditions.fechaEntrega.$gte = d.toISOString(); }
            if (filters.fechaHasta) { const d = new Date(filters.fechaHasta); d.setHours(23,59,59,999);  conditions.fechaEntrega.$lte = d.toISOString(); }
          }

          // ── Líder (responsable de la OT) ────────────────────────────────
          const liderQ = liderQueryFromText(filters.lider);
          if (liderQ) {
            const ids = await lookupUserIds(liderQ);
            conditions.responsable = { $in: ids };
          }
        }

        const url = isTab1
          ? `/Programacion/paginar?page=${page}&perPage=${perPage}`
          : `/Ot/paginar?page=${page}&perPage=${perPage}`;

        // Ordenar por la fecha en que se debe trabajar (más reciente primero)
        const sort = isTab1 ? { fechaInicio: -1 } : { fechaEntrega: -1 };

        const response = await paginate(url, { conditions, sort });

        const raw = response.data.data;
        const result = {
          results:    raw.results        || [],
          total:      raw.total_elements || 0,
          totalPages: raw.total_pages    || 0,
          next:       raw.next           || null,
          prev:       raw.prev           || null,
          hasNextPage: !!raw.next,
          hasPrevPage: !!raw.prev,
          page,
          perPage
        };

        setCached(cacheKey, result);
        setData(result);
      } catch (err) {
        console.error(`Error cargando ${tab}:`, err);
        Alertassw('error', 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [CACHE_KEY_TAB1, CACHE_KEY_TAB2, getCached, setCached, lookupUserIds, permisos, userId]
  );

  // ── Carga inicial y al cambiar de tab ────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'misOT') {
      doLoadData('misOT', tab1Filters);
    } else {
      doLoadData('otAbiertas', tab2Filters);
    }
    // Solo se re-ejecuta cuando cambia activeTab.
    // Los cambios de filtro llaman a doLoadData directamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Cambio de tab ────────────────────────────────────────────────────────
  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;
    // Limpiar caché del tab que se abandona
    invalidate(activeTab === 'misOT' ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    localStorage.setItem(TAB_STORAGE_KEY, newTab);
    setActiveTab(newTab);
    // doLoadData se ejecutará desde el useEffect cuando activeTab cambie
  };

  // ── Cambio de filtro ─────────────────────────────────────────────────────
  const handleFilterChange = (tab, field, value) => {
    const isTab1         = tab === 'misOT';
    const currentFilters = isTab1 ? tab1Filters : tab2Filters;
    const newFilters     = { ...currentFilters, [field]: value, page: 1 };

    if (isTab1) {
      setTab1Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB1, JSON.stringify(newFilters));
    } else {
      setTab2Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB2, JSON.stringify(newFilters));
    }

    invalidate(isTab1 ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    doLoadData(tab, newFilters, true);
  };

  // ── Cambio de página ─────────────────────────────────────────────────────
  const handlePageChange = (tab, page) => {
    const isTab1         = tab === 'misOT';
    const currentFilters = isTab1 ? tab1Filters : tab2Filters;
    const newFilters     = { ...currentFilters, page };

    if (isTab1) {
      setTab1Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB1, JSON.stringify(newFilters));
    } else {
      setTab2Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB2, JSON.stringify(newFilters));
    }

    invalidate(isTab1 ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    doLoadData(tab, newFilters, true);
  };

  // ── Cambio de items por página ───────────────────────────────────────────
  const handlePerPageChange = (tab, perPage) => {
    const isTab1         = tab === 'misOT';
    const currentFilters = isTab1 ? tab1Filters : tab2Filters;
    const newFilters     = { ...currentFilters, perPage, page: 1 };

    if (isTab1) {
      setTab1Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB1, JSON.stringify(newFilters));
    } else {
      setTab2Filters(newFilters);
      localStorage.setItem(FILTER_KEY_TAB2, JSON.stringify(newFilters));
    }

    invalidate(isTab1 ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    doLoadData(tab, newFilters, true);
  };

  // ── Refrescar (fuerza recarga) ───────────────────────────────────────────
  const handleRefresh = (tab) => {
    const filters = tab === 'misOT' ? tab1Filters : tab2Filters;
    invalidate(tab === 'misOT' ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    doLoadData(tab, filters, true);
  };

  // ── Resetear filtros ─────────────────────────────────────────────────────
  const handleResetFilters = (tab) => {
    const defaults = tab === 'misOT' ? DEFAULT_TAB1_FILTERS : DEFAULT_TAB2_FILTERS;
    if (tab === 'misOT') {
      setTab1Filters(defaults);
      localStorage.setItem(FILTER_KEY_TAB1, JSON.stringify(defaults));
    } else {
      setTab2Filters(defaults);
      localStorage.setItem(FILTER_KEY_TAB2, JSON.stringify(defaults));
    }
    invalidate(tab === 'misOT' ? CACHE_KEY_TAB1 : CACHE_KEY_TAB2);
    doLoadData(tab, defaults, true);
  };

  // ── API pública ──────────────────────────────────────────────────────────
  return {
    activeTab,
    handleTabChange,
    tab1: {
      data:            tab1Data,
      loading:         tab1Loading,
      filters:         tab1Filters,
      onFilterChange:  (field, value) => handleFilterChange('misOT', field, value),
      onPageChange:    (page)         => handlePageChange('misOT', page),
      onPerPageChange: (pp)           => handlePerPageChange('misOT', pp),
      onRefresh:       ()             => handleRefresh('misOT'),
      onResetFilters:  ()             => handleResetFilters('misOT')
    },
    tab2: {
      data:            tab2Data,
      loading:         tab2Loading,
      filters:         tab2Filters,
      onFilterChange:  (field, value) => handleFilterChange('otAbiertas', field, value),
      onPageChange:    (page)         => handlePageChange('otAbiertas', page),
      onPerPageChange: (pp)           => handlePerPageChange('otAbiertas', pp),
      onRefresh:       ()             => handleRefresh('otAbiertas'),
      onResetFilters:  ()             => handleResetFilters('otAbiertas')
    }
  };
};
